/**
 * Task #1762 — Zod schemas per gestione admin master cross-tenant.
 *
 * Source of truth dei contratti HTTP per `/api/master/master-users`,
 * `/api/master/master-roles`, `/api/master/master-permissions` e
 * `/api/auth/master/2fa/*`. Lato BE: `validate({ body|params|query })` su
 * ogni rotta. Lato FE: parsing via `z.infer<>` per intellisense full.
 *
 * Convenzioni:
 *   - Tutte le azioni distruttive admin-on-admin (reset_password,
 *     disable_2fa, revoke_sessions, delete) richiedono `reason` 10..500 chars.
 *   - Email lowercase + trim al boundary.
 *   - Password ≥ 12 chars con almeno una maiuscola, una cifra, un simbolo.
 *   - `role_id` UUID opzionale: se assente, fallback al ruolo legacy `role` string.
 */
import { z } from 'zod';

export const masterUserRoleSchema = z.enum(['super_admin', 'admin', 'manager', 'user', 'agent']);
export const masterUserStatusSchema = z.enum(['active', 'inactive', 'locked', 'pending_verification']);
export const masterAuthProviderSchema = z.enum(['local', 'ldap']);

const emailSchema = z
  .string()
  .min(3, 'Email troppo corta')
  .max(255, 'Email troppo lunga')
  .email('Email non valida')
  .transform((s) => s.trim().toLowerCase());

export const reasonSchema = z
  .string({ required_error: 'Il campo "reason" è obbligatorio per le azioni distruttive' })
  .trim()
  .min(10, 'Il motivo deve avere almeno 10 caratteri')
  .max(500, 'Il motivo non può superare 500 caratteri');

const nameSchema = z.string().trim().min(1, 'Campo obbligatorio').max(100);
const optionalNameSchema = z.string().trim().min(1).max(100).optional();
const uuidSchema = z.string().uuid('UUID non valido');

// ============================================================================
// MASTER USERS
// ============================================================================

export const masterUserListQuerySchema = z.object({
  search: z.string().trim().max(255).optional(),
  role: masterUserRoleSchema.optional(),
  status: masterUserStatusSchema.optional(),
  totp_enabled: z
    .union([z.boolean(), z.enum(['true', 'false'])])
    .transform((v) => (typeof v === 'boolean' ? v : v === 'true'))
    .optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});
export type MasterUserListQuery = z.infer<typeof masterUserListQuerySchema>;

export const masterUserIdParamsSchema = z.object({ userId: uuidSchema });
export type MasterUserIdParams = z.infer<typeof masterUserIdParamsSchema>;

/**
 * Create utente master: nessuna password nel body. Lato BE viene emesso un
 * token di reset password (master.password_reset_tokens) e inviata email
 * "imposta la tua password" tramite emailService.sendPasswordResetEmail.
 * Il primo login forza force_password_change=true.
 */
export const masterUserCreateRequestSchema = z.object({
  email: emailSchema,
  first_name: nameSchema,
  last_name: nameSchema,
  role: masterUserRoleSchema.default('user'),
  role_id: uuidSchema.optional(),
  status: masterUserStatusSchema.default('active'),
  auth_provider: masterAuthProviderSchema.default('local'),
  is_local_auth_allowed: z.boolean().default(true),
  mobile: z.string().trim().max(50).optional(),
});
export type MasterUserCreateRequest = z.infer<typeof masterUserCreateRequestSchema>;

export const masterUserUpdateRequestSchema = z
  .object({
    first_name: optionalNameSchema,
    last_name: optionalNameSchema,
    role: masterUserRoleSchema.optional(),
    role_id: uuidSchema.nullable().optional(),
    status: masterUserStatusSchema.optional(),
    auth_provider: masterAuthProviderSchema.optional(),
    is_local_auth_allowed: z.boolean().optional(),
    mobile: z.string().trim().max(50).nullable().optional(),
  })
  .refine((o) => Object.keys(o).length > 0, { message: 'Nessun campo da aggiornare' });
export type MasterUserUpdateRequest = z.infer<typeof masterUserUpdateRequestSchema>;

/**
 * Reset password admin-on-admin: NON setta direttamente la password. Emette
 * un token in master.password_reset_tokens, invia email al target con il
 * link di reset, invalida le sessioni attive. Il target completa il reset
 * via il flusso pubblico /api/auth/master/reset-password. Reason obbligatorio.
 */
export const masterUserResetPasswordRequestSchema = z.object({
  reason: reasonSchema,
});
export type MasterUserResetPasswordRequest = z.infer<typeof masterUserResetPasswordRequestSchema>;

export const masterUserDisableTwoFactorRequestSchema = z.object({
  reason: reasonSchema,
});
export type MasterUserDisableTwoFactorRequest = z.infer<typeof masterUserDisableTwoFactorRequestSchema>;

export const masterUserRevokeSessionsRequestSchema = z.object({
  reason: reasonSchema,
  session_id: uuidSchema.optional(),
});
export type MasterUserRevokeSessionsRequest = z.infer<typeof masterUserRevokeSessionsRequestSchema>;

export const masterUserDeleteRequestSchema = z.object({
  reason: reasonSchema,
});
export type MasterUserDeleteRequest = z.infer<typeof masterUserDeleteRequestSchema>;

// ============================================================================
// CROSS-TENANT LEGACY ACTIONS (Task #1788)
// Endpoint legacy `/api/master/users/:id/*` su `public.users` con audit reason.
// reset_password: reason obbligatorio. impersonate: reason opzionale.
// ============================================================================

export const crossTenantResetPasswordRequestSchema = z.object({
  reason: reasonSchema,
});
export type CrossTenantResetPasswordRequest = z.infer<typeof crossTenantResetPasswordRequestSchema>;

export const crossTenantImpersonateRequestSchema = z.object({
  reason: reasonSchema.optional(),
});
export type CrossTenantImpersonateRequest = z.infer<typeof crossTenantImpersonateRequestSchema>;

// ============================================================================
// MASTER ROLES
// ============================================================================

export const masterRoleIdParamsSchema = z.object({ roleId: uuidSchema });
export type MasterRoleIdParams = z.infer<typeof masterRoleIdParamsSchema>;

export const masterRoleCreateRequestSchema = z.object({
  name: nameSchema,
  description: z.string().trim().max(500).optional(),
  role_kind: z.string().trim().max(50).optional(),
  permission_ids: z.array(uuidSchema).max(500).default([]),
});
export type MasterRoleCreateRequest = z.infer<typeof masterRoleCreateRequestSchema>;

export const masterRoleUpdateRequestSchema = z
  .object({
    name: optionalNameSchema,
    description: z.string().trim().max(500).nullable().optional(),
    role_kind: z.string().trim().max(50).nullable().optional(),
  })
  .refine((o) => Object.keys(o).length > 0, { message: 'Nessun campo da aggiornare' });
export type MasterRoleUpdateRequest = z.infer<typeof masterRoleUpdateRequestSchema>;

export const masterRolePermissionAssignmentRequestSchema = z.object({
  permission_ids: z.array(uuidSchema).max(500),
  mode: z.enum(['replace', 'add', 'remove']).default('replace'),
});
export type MasterRolePermissionAssignmentRequest = z.infer<
  typeof masterRolePermissionAssignmentRequestSchema
>;

// ============================================================================
// MASTER PERMISSIONS
// ============================================================================

export const masterPermissionListQuerySchema = z.object({
  category: z.string().trim().max(100).optional(),
  search: z.string().trim().max(255).optional(),
});
export type MasterPermissionListQuery = z.infer<typeof masterPermissionListQuerySchema>;

// ============================================================================
// 2FA SELF-SERVICE (master)
// ============================================================================

export const masterTwoFactorSetupRequestSchema = z.object({}).optional();
export type MasterTwoFactorSetupRequest = z.infer<typeof masterTwoFactorSetupRequestSchema>;

export const masterTwoFactorEnableRequestSchema = z.object({
  code: z.string().regex(/^\d{6}$/, 'Codice TOTP deve essere 6 cifre'),
});
export type MasterTwoFactorEnableRequest = z.infer<typeof masterTwoFactorEnableRequestSchema>;

/**
 * Disable 2FA self-service: richiede SIA password (proof-of-knowledge) SIA
 * codice TOTP corrente (proof-of-possession). Difesa in profondità: una
 * password compromessa da sola non basta a smontare il 2FA.
 */
export const masterTwoFactorDisableRequestSchema = z.object({
  password: z.string().min(1, 'Password richiesta'),
  code: z.string().regex(/^\d{6}$/, 'Codice TOTP deve essere 6 cifre'),
});
export type MasterTwoFactorDisableRequest = z.infer<typeof masterTwoFactorDisableRequestSchema>;

/**
 * Regenerate backup codes: richiede codice TOTP corrente (proof-of-possession).
 * I vecchi backup codes vengono invalidati e sostituiti.
 */
export const masterTwoFactorRegenerateBackupRequestSchema = z.object({
  code: z.string().regex(/^\d{6}$/, 'Codice TOTP deve essere 6 cifre'),
});
export type MasterTwoFactorRegenerateBackupRequest = z.infer<
  typeof masterTwoFactorRegenerateBackupRequestSchema
>;

/**
 * Toggle "2FA obbligatorio" per il piano master. Il flag è salvato in
 * `public.tenants.settings.two_factor_enabled` sulla riga del tenant master:
 * quando ON, gli admin master non iscritti ricevono `requiresTOTPSetup` al login.
 */
export const masterTwoFactorRequirementUpdateSchema = z.object({
  enabled: z.boolean(),
});
export type MasterTwoFactorRequirementUpdate = z.infer<
  typeof masterTwoFactorRequirementUpdateSchema
>;

/**
 * Change password self-service (master, autenticato). Richiede password
 * corrente + nuova password con policy standard (8+ con complessità).
 * Speculare alla /change-password tenant, ma scoped su `master.users`.
 */
export const masterChangePasswordRequestSchema = z.object({
  currentPassword: z.string().min(1, 'Password corrente obbligatoria').max(128),
  newPassword: z.string()
    .min(8, 'La password deve avere almeno 8 caratteri')
    .max(128, 'La password non può superare 128 caratteri')
    .regex(/[A-Z]/, 'La password deve contenere almeno una lettera maiuscola')
    .regex(/[a-z]/, 'La password deve contenere almeno una lettera minuscola')
    .regex(/[0-9]/, 'La password deve contenere almeno un numero')
    .regex(/[^A-Za-z0-9]/, 'La password deve contenere almeno un carattere speciale'),
});
export type MasterChangePasswordRequest = z.infer<typeof masterChangePasswordRequestSchema>;
